import { BaseElement } from './base-element.js';

/**
 * `<sim-badge>` — the persistent honesty chip.
 *
 * The People Engine is a mock-always simulation in v1, so the badge renders the
 * single `simulation` state; the live/partial modes (and the per-layer tooltip
 * breakdown the Idea Engine badge grew in IDEA-11) join when the first live
 * adapter exists. It is a STATIC informational chip, not a live region; the
 * explanation rides in `aria-label`/`title`. Render is DOM construction +
 * `textContent` only — never raw HTML (ADR 0008).
 */
export class SimBadge extends BaseElement {
  static readonly tagName = 'sim-badge';
  static get observedAttributes(): string[] {
    return ['text', 'label'];
  }

  connectedCallback(): void {
    this.adoptStyles(`
      :host {
        display: inline-flex;
        align-items: center;
        gap: var(--space-1);
        padding-block: var(--space-1);
        padding-inline: var(--space-2);
        border-radius: var(--radius);
        font-family: var(--font-display);
        font-size: var(--font-size-1);
        font-weight: 700;
        letter-spacing: 0.1em;
        text-transform: uppercase;
        color: var(--color-bg);
        background: var(--color-sim);
        box-shadow: 0 0 14px rgba(255, 176, 32, 0.4);
        cursor: default;
      }
      :host(:focus-visible) {
        outline: 2px solid var(--color-focus);
        outline-offset: 2px;
      }
    `);
    this.render();
  }

  attributeChangedCallback(): void {
    if (this.isConnected) this.render();
  }

  private render(): void {
    const text = this.getAttribute('text') ?? '';
    const label = this.getAttribute('label') ?? text;
    this.setAttribute('role', 'img');
    this.setAttribute('aria-label', label);
    this.setAttribute('title', label);
    if (!this.hasAttribute('tabindex')) this.setAttribute('tabindex', '0');

    const chip = document.createElement('span');
    chip.textContent = text;
    this.root.replaceChildren(chip);
  }
}
